import { constants } from "@/constants";
import Logger from "@/services/logger";

// In-memory replacement used when localStorage is unavailable (Safari private
// browsing, sandboxed webviews, cookies blocked). Data lives for the session
// only, which keeps the cart and forms working without persistence.
const memoryStorage = {
  data: new Map(),
  getItem(key) {
    return this.data.has(key) ? this.data.get(key) : null;
  },
  setItem(key, value) {
    this.data.set(key, String(value));
  },
  removeItem(key) {
    this.data.delete(key);
  },
};

let resolvedStorage = null;

function safeStorage() {
  if (resolvedStorage) {
    return resolvedStorage;
  }
  try {
    const probeKey = "__mls_probe__";
    window.localStorage.setItem(probeKey, "1");
    window.localStorage.removeItem(probeKey);
    resolvedStorage = window.localStorage;
  } catch (e) {
    resolvedStorage = memoryStorage;
  }
  return resolvedStorage;
}

export default class MultiLocalStorage {
  static checkCleanOrderHistory() {
    // clean local storage if it's too old
    const localStorageName = constants.ECOM_ORDER_HISTORY;

    const localStorageGc = safeStorage().getItem(constants.LOCAL_STORAGE_GC);
    if (!localStorageGc) {
      safeStorage().removeItem(localStorageName);
      safeStorage().setItem(
        constants.LOCAL_STORAGE_GC,
        JSON.stringify({ cleaningOrderCheck: new Date().getTime() }),
      );
    } else {
      const localStorageGcJson = JSON.parse(localStorageGc);
      if (
        localStorageGcJson.cleaningOrderCheck <
        new Date().getTime() - 1000 * 60 * 60 * 24
      ) {
        // 1 day
        safeStorage().removeItem(localStorageName);
        safeStorage().setItem(
          constants.LOCAL_STORAGE_GC,
          JSON.stringify({ cleaningOrderCheck: new Date().getTime() }),
        );
      } else {
        safeStorage().setItem(
          constants.LOCAL_STORAGE_GC,
          JSON.stringify({ cleaningOrderCheck: new Date().getTime() }),
        );
      }
    }
  }

  static get(localStorageName, domainId, withTtl = false) {
    if (!domainId) {
      console.warn("Trying to get unscoped domain value from localStorage !");
    }
    let finalData = null;

    const rawData = safeStorage().getItem(localStorageName);
    if (rawData !== null) {
      let data = null;
      try {
        data = JSON.parse(rawData);
      } catch (e) {
        console.error(e);
        Logger.error("Corrupted localStorage entry removed", {
          key: localStorageName,
          error: e.toString(),
        });
        safeStorage().removeItem(localStorageName);
        return null;
      }

      if (data.expiration) {
        if (data.expiration < Date.now()) {
          safeStorage().removeItem(localStorageName);
          return null;
        }
        data = data.data;
      }
      if (data.length !== undefined && domainId) {
        finalData = data.find((item) => item.domainId === domainId);
      } else if (data.length !== undefined) {
        finalData = data[0];
      } else {
        finalData = data;
      }
    }

    if (finalData && finalData.fromArray) {
      delete finalData.fromArray;
      delete finalData.domainId;
      finalData = Object.values(finalData);
    }

    return finalData;
  }

  static set(
    localStorageName,
    domainId,
    newData,
    merge = false,
    ttlDuration = 0,
  ) {
    if (!domainId) {
      console.warn("Trying to set unscoped domain value from localStorage !");
    }
    if (Array.isArray(newData)) {
      newData = Object.assign({}, newData); // transform array to object
      newData.fromArray = true;
    }
    if (domainId) {
      newData.domainId = domainId;
    }

    const rawData = safeStorage().getItem(localStorageName);
    let existingData = null;
    if (rawData !== null) {
      try {
        existingData = JSON.parse(rawData);
      } catch (e) {
        console.error(e);
        Logger.error("Corrupted localStorage entry replaced", {
          key: localStorageName,
          error: e.toString(),
        });
        safeStorage().removeItem(localStorageName);
      }
    }

    if (existingData !== null) {
      let data = existingData;
      let expiration = null;
      if (data.expiration) {
        expiration = data.expiration;
        data = data.data;
      }
      if (data.length !== undefined) {
        // local storage is an array of data
        if (domainId) {
          let scopedData = data.find((cart) => cart.domainId === domainId);
          if (scopedData) {
            // data exists for the domain in local storage
            if (merge) {
              Object.assign(scopedData, newData); // data is updated by reference (and scopedData is merged with old data)
            } else {
              // ensure that object is empty before adding new data
              for (const key in scopedData) {
                delete scopedData[key];
              }
              for (const key in newData) {
                scopedData[key] = newData[key];
              }
            }
          } else {
            // there is no data for current domain in local storage
            data.push(newData);
          }
        } else {
          // if, for any reason, we don't have a domainId, we just push the data
          data.push(newData);
        }
      } else {
        // old data format when local storage was a single data => switch to array of data
        data = [newData];
      }

      if (expiration) {
        data = {
          data,
          expiration,
        };
      }

      safeStorage().setItem(localStorageName, JSON.stringify(data));
    } else {
      let data = null;
      if (ttlDuration) {
        const ttl = Date.now() + ttlDuration;
        data = {
          data: [newData],
          expiration: ttl,
        };
      } else {
        data = [newData];
      }
      safeStorage().setItem(localStorageName, JSON.stringify(data));
    }
  }
}
